import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Search, UserRound } from "lucide-react";
import { fmtDate } from "@/config/crm";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";

export default function Patients() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [owners, setOwners] = useState([]);
  const [dialog, setDialog] = useState(false);

  useEffect(() => { api.get("/leads/owners").then(({ data }) => setOwners(data.owners)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = search.trim() ? { search: search.trim() } : {};
    try {
      const { data } = await api.get("/patients", { params });
      setPatients(data.patients);
    } finally { setLoading(false); }
  }, [search]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="animate-fade-up space-y-6" data-testid="patients-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Patients</h1>
          <p className="mt-1 text-sm text-slate-500"><span data-testid="patients-total" className="font-medium text-slate-700">{patients.length}</span> patient records</p>
        </div>
        <Button onClick={() => setDialog(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-patient-button">
          <Plus className="h-4 w-4" /> Add Patient
        </Button>
      </div>

      <Card className="p-4 shadow-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search by name, phone or patient ID…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="patients-search" />
        </div>
      </Card>

      <Card className="hidden overflow-hidden shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="pl-6 text-xs uppercase tracking-wider text-slate-400">Patient</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Patient ID</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">City</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Source</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(5)].map((_, i) => <TableRow key={i}><TableCell colSpan={5} className="px-6"><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
              : patients.length === 0 ? <TableRow><TableCell colSpan={5} className="py-16 text-center text-sm text-slate-400" data-testid="patients-empty">No patients yet.</TableCell></TableRow>
              : patients.map((p) => (
                <TableRow key={p.id} className="cursor-pointer border-slate-100" onClick={() => navigate(`/patients/${p.id}`)} data-testid="patient-row">
                  <TableCell className="pl-6">
                    <p className="font-medium text-slate-900">{p.full_name}</p>
                    <p className="font-mono text-xs text-slate-400">{p.phone}</p>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{p.patient_code}</TableCell>
                  <TableCell className="text-sm text-slate-600">{p.city || "—"}</TableCell>
                  <TableCell>{p.lead_source ? <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">{p.lead_source}</Badge> : <span className="text-xs text-slate-400">Direct</span>}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-500">{fmtDate(p.created_at)}</TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <div className="space-y-3 md:hidden">
        {loading ? [...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)
          : patients.map((p) => (
            <Card key={p.id} className="p-4 shadow-sm" onClick={() => navigate(`/patients/${p.id}`)} data-testid="patient-card">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><UserRound className="h-4 w-4" /></div>
                <div>
                  <p className="font-medium text-slate-900">{p.full_name}</p>
                  <p className="font-mono text-xs text-slate-400">{p.phone} · {p.patient_code}</p>
                </div>
              </div>
            </Card>
          ))}
      </div>

      <PatientFormDialog open={dialog} onOpenChange={setDialog} patient={null} owners={owners} onSaved={load} />
    </div>
  );
}
